import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/react-dom-interactions'
import { getHistoryInstance } from 'hooks/useHistory'
import { EditorState } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { createRoot, Root } from 'react-dom/client'
import { Router } from 'react-router-dom'
import { EDITOR_CONTAINER_ID } from 'screens/ProseEdit/Constants'
import { ArticleFlat, Layer } from 'types'
import { INLINE_LINK_ID, INLINE_LINK_MENU_PLUGIN_KEY } from './Constants'
import Styles from './InlineLinkMenu.module.scss'
import { InlineLinkMenuContent } from './InlineLinkMenuContent'
import { LinkMenuArgs } from './Types'

export class InlineLinkMenuView {
  tooltip: HTMLDivElement
  initialized = false
  article?: ArticleFlat
  layer?: Layer
  autoUpdateCleanup?: () => void
  root: Root

  constructor({ view, article, layer }: LinkMenuArgs) {
    this.article = article
    this.layer = layer

    this.tooltip = document.createElement('div')
    this.tooltip.id = INLINE_LINK_ID
    this.tooltip.className = Styles.positionContainer
    this.root = createRoot(this.tooltip)

    if (view.dom.parentNode) {
      view.dom.parentNode.appendChild(this.tooltip)
    }

    this.addToDom(view)

    this.update(view)
  }

  addToDom = (view: EditorView) => {
    if (view.dom.parentNode) {
      view.dom.parentNode.appendChild(this.tooltip)
      this.initialized = true
    }
  }

  update(view: EditorView, lastState?: EditorState) {
    const { show, link } = INLINE_LINK_MENU_PLUGIN_KEY.getState(view.state)

    if (this.tooltip.style.display === 'none') {
      this.tooltip.classList.remove(Styles.positionContainerAnimated)
    }
    if (!this.initialized) {
      this.addToDom(view)
    }

    if (!view.state.selection.empty || !show || !link) {
      this.tooltip.style.display = 'none'
      return
    }

    if (!this.article || !this.layer) {
      return
    }

    this.tooltip.style.display = ''
    const crossAxisOffset = view.editable ? -55 : 0
    const shiftBoundary = document.getElementById(EDITOR_CONTAINER_ID)
    if (!shiftBoundary) reportError('No editor boundary found rendering format bar')

    if (this.autoUpdateCleanup) this.autoUpdateCleanup()
    this.autoUpdateCleanup = autoUpdate(link, this.tooltip, () => {
      computePosition(link, this.tooltip, {
        placement: 'top-start',
        middleware: [
          offset({ crossAxis: crossAxisOffset, mainAxis: 2 }),
          shift({ boundary: shiftBoundary ?? document.body }),
          flip(),
        ],
      }).then(({ x, y }) => {
        const top = Math.max(2, Math.round(y))
        const left = Math.round(x)
        Object.assign(this.tooltip.style, {
          transform: `translate(${left}px, ${top}px)`,
        })
      })
    })

    this.root.render(
      <Router history={getHistoryInstance()}>
        <InlineLinkMenuContent article={this.article} view={view} currentLink={link.href} />
      </Router>,
    )

    this.tooltip.classList.add(Styles.positionContainerAnimated)
  }

  destroy() {
    if (this.autoUpdateCleanup) this.autoUpdateCleanup()
    setTimeout(() => this.root.unmount(), 0)
    this.tooltip.remove()
  }
}
